/*
  embed.js  (hosted payment page, src/utils/embed.js)
  ---------------------------------------------------
  When the dashboard opens the hosted checkout inside its top-up iframe, it
  appends query params to the checkout URL:

    ?_tranId=...&embedded=1&theme=dark&parentOrigin=https://dashboard.example

  This helper reads them, persists them (internal navigations can drop the
  query string), exposes the embedded context, and posts events back to the
  parent. Same message contract the dashboard checkout modal already listens for:

    { source: "wayaquick-checkout", event: "cancel" | "close" | "success" | "failed" }
*/

// embed.js — wrap every write defensively
const safeSet = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    // Storage blocked/unavailable (private mode, partitioned third-party
    // context, quota). Non-fatal — URL params remain the source of truth.
  }
};

export const getEmbedContext = () => {
  const params = new URLSearchParams(window.location.search);

  let inIframe = false;
  try {
    inIframe = window.self !== window.top;
  } catch (e) {
    inIframe = true;
  }

  const paramFlag =
    params.get("embedded") === "1" || params.get("wq_source") === "wayatopup";
  const paramTheme = params.get("theme");
  const paramParent = params.get("parentOrigin");
  const paramPublicKey = params.get("publicKey");
  
  if (paramFlag) safeSet("wq_embedded", "1");
  if (paramTheme) safeSet("wq_theme", paramTheme);
  if (paramParent) safeSet("wq_parentOrigin", paramParent);
  if (paramParent) safeSet("PUBLIC_KEY", paramPublicKey);

  

  let storedEmbedded, storedTheme, storedParent, storedPublicKey;
  try {
    storedEmbedded = sessionStorage.getItem("wq_embedded");
    storedTheme = sessionStorage.getItem("wq_theme");
    storedParent = sessionStorage.getItem("wq_parentOrigin");
    storedPublicKey = sessionStorage.getItem("PUBLIC_KEY");
  } catch (e) {
    // reads generally don't throw, but stay defensive
  }

  const isEmbedded = inIframe && (paramFlag || storedEmbedded === "1");
  const theme = paramTheme || storedTheme || null;
  const parentOrigin = paramParent || storedParent || "*";
  const isTestMode = (storedPublicKey || paramPublicKey || "").includes("_TEST_");


  return { isEmbedded, theme, parentOrigin, isTestMode };
};
  
  export const notifyParent = (event, extra = {}) => {
    try {
      if (window.parent && window.parent !== window) {
        const { parentOrigin } = getEmbedContext();
        window.parent.postMessage(
          { source: "wayaquick-checkout", event, ...extra },
          parentOrigin
        );
      }
    } catch (e) {
      // no parent / blocked: nothing to do
    }
  };
  
  // Sets <html class="wq-embedded" data-wq-theme="dark"> so the stylesheet can
  // react. Safe to call more than once. Call as early as possible (index.js)
  // to avoid a flash of the light theme.
  export const applyEmbedTheme = () => {
    const { isEmbedded, theme } = getEmbedContext();
    const root = document.documentElement;
    if (isEmbedded) root.classList.add("wq-embedded");
    if (theme === "dark" || theme === "light") {
      root.setAttribute("data-wq-theme", theme);
    }
  };





  // embed.js — add this alongside the existing exports

// Rebuilds a query string carrying every embed signal currently known,
// pulled from the URL first (source of truth, survives third-party storage
// partitioning) and sessionStorage second (best-effort cache only).
export const getEmbedQueryString = () => {
  const params = new URLSearchParams(window.location.search);
  const qs = new URLSearchParams();

  const embedded =
    params.get("embedded") === "1" || sessionStorage.getItem("wq_embedded") === "1";
  const theme = params.get("theme") || sessionStorage.getItem("wq_theme");
  const parentOrigin =
    params.get("parentOrigin") || sessionStorage.getItem("wq_parentOrigin");
  const source = params.get("wq_source") || sessionStorage.getItem("wq_source");
  const publicKey = params.get("publicKey") || sessionStorage.getItem("PUBLIC_KEY");

  if (embedded) qs.set("embedded", "1");
  if (theme) qs.set("theme", theme);
  if (parentOrigin) qs.set("parentOrigin", parentOrigin);
  if (source) qs.set("wq_source", source);
  if (publicKey) qs.set("publicKey", publicKey);

  return qs.toString();
};
  